import { useContext, useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
// import DateFilterToolbar from 'Presentation/Components/CustomToolbar/DateFilter'
import Toolbar from './Components/Toolbar'

import { UIContext } from 'Context/UI'
import { TableContext } from 'Context/Table'
import { useQuotationStore } from 'Store/Quotation'
import { shallow } from 'zustand/shallow'
import Swal from 'sweetalert2'
import moment from 'moment'
import 'moment-timezone'

const INITIAL_FILTER_STATE: {
	startPeriod: string | null
	endPeriod: string | null
} = {
	startPeriod: moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD HH:mm'),
	endPeriod: moment().format('YYYY-MM-DD HH:mm'),
}

const ViewModel = () => {
	const navigate = useNavigate()
	const { rawData, getAll, remove, clearState } = useQuotationStore(
		state => ({
			rawData: state.data,
			getAll: state.getAll,
			remove: state.remove,
			clearState: state.clearState,
		}),
		shallow
	)
	const { updateLoading, updateError } = useContext(TableContext)
	const { setShowContentToolbar, setCustomContentToolbar } = useContext(UIContext)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [currentPage, setCurrentPage] = useState<number>(1)
	const [isUseDateFilter, setIsUseDateFilter] = useState<boolean>(true)
	const [filter, setFilter] = useState(INITIAL_FILTER_STATE)

	const data = useMemo(() => {
		const _rawData = [...rawData]
		return _rawData.splice((currentPage - 1) * 10, 10)
	}, [rawData, currentPage])

	const fetchData = () => {
		setIsLoading(true)
		updateLoading(true)
		updateError(false)

		getAll({
			createdByRole: 'supplier',
		}).then(({ data, success }) => {
			if (!success) {
				Swal.fire({
					title: 'เกิดข้อผิดพลาด',
					text: data ?? 'ไม่สามารถดึงข้อมูลได้',
					icon: 'error',
					showConfirmButton: false,
					customClass: {
						confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
					},
					timer: 1500,
				})

				updateError(true)
				return
			} else {
				setIsLoading(false)
				updateLoading(false)
				updateError(false)
			}
		})
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const onChangeDateFilter = (
		type: 'today' | 'thisWeek' | 'thisMonth' | 'custom',
		key?: string,
		value?: any
	) => {
		if (type === 'today') {
			setFilter({
				startPeriod: moment()
					.tz('Asia/Bangkok')
					.set({
						hour: 0,
						minute: 0,
						second: 0,
						millisecond: 0,
					})
					.format('YYYY-MM-DD HH:mm'),
				endPeriod: moment()
					.tz('Asia/Bangkok')
					.set({
						hour: 23,
						minute: 59,
						second: 59,
						millisecond: 59,
					})
					.format('YYYY-MM-DD HH:mm'),
			})
		} else if (type === 'thisWeek') {
			// This week is start from Thrusday to Wednesday
			setFilter({
				startPeriod: moment()
					.tz('Asia/Bangkok')
					.startOf('week')
					.add(3, 'days')
					.set({
						hour: 0,
						minute: 0,
						second: 0,
						millisecond: 0,
					})
					.format('YYYY-MM-DD HH:mm'),
				endPeriod: moment()
					.tz('Asia/Bangkok')
					.endOf('week')
					.add(3, 'days')
					.set({
						hour: 23,
						minute: 59,
						second: 59,
						millisecond: 59,
					})
					.format('YYYY-MM-DD HH:mm'),
			})
		} else if (type === 'thisMonth') {
			setFilter({
				startPeriod: moment().tz('Asia/Bangkok').startOf('month').format('YYYY-MM-DD HH:mm'),
				endPeriod: moment().tz('Asia/Bangkok').endOf('month').format('YYYY-MM-DD HH:mm'),
			})
		} else if (type === 'custom') {
			if (key === 'startPeriod') {
				const finale =
					value !== null
						? moment(value)
								.tz('Asia/Bangkok')
								.set({
									hour: 0,
									minute: 0,
									second: 0,
									millisecond: 0,
								})
								.format('YYYY-MM-DD HH:mm HH:mm')
						: null

				setFilter({
					...filter,
					startPeriod: finale,
				})
			} else if (key === 'endPeriod') {
				const finale =
					value !== null
						? moment(value)
								.tz('Asia/Bangkok')
								.set({
									hour: 23,
									minute: 59,
									second: 59,
									millisecond: 59,
								})
								.format('YYYY-MM-DD HH:mm HH:mm')
						: null

				setFilter({
					...filter,
					endPeriod: finale,
				})
			}
		}
	}

	const onChangeFilter = (key: string, value: any) => {
		if (key === 'startPeriod' || key === 'endPeriod') {
			const finale =
				value !== null
					? moment(value)
							.tz('Asia/Bangkok')
							.set({
								hour: key === 'startPeriod' ? 0 : 23,
								minute: key === 'startPeriod' ? 0 : 59,
								second: key === 'startPeriod' ? 0 : 59,
								millisecond: key === 'startPeriod' ? 0 : 59,
							})
							.format('YYYY-MM-DD HH:mm')
					: null

			setFilter(prev => ({ ...prev, [key]: finale }))

			if (value !== null) {
				setIsUseDateFilter(false)
			}
		} else {
			setFilter(prev => ({ ...prev, [key]: value }))
		}
	}

	const viewDetail = (index: number) => {
		navigate(`/documents/quotation/${data[index].uuId}`)
	}

	const createQuotation = () => {
		navigate('/documents/quotation/create')
	}

	const goToPage = (page: number | null) => {
		if (page) {
			setCurrentPage(page)
			document.getElementById('request-list-anchor')?.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
			})
		}
	}

	const onDelete = (index: number) => {
		Swal.fire({
			title: 'คุณแน่ใจหรือไม่?',
			text: 'คุณต้องการลบใบเสนอราคานี้หรือไม่?',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'ใช่, ฉันต้องการลบ',
			cancelButtonText: 'ยกเลิก',
			customClass: {
				confirmButton: 'btn btn-sm btn-kumopack-error-600 text-kumopack-base-white',
				cancelButton: 'btn btn-sm white-button text-kumopack-grey-700',
			},
		}).then(result => {
			if (result.isConfirmed) {
				remove(data[index].uuId).then(({ success, data }) => {
					if (!success) {
						Swal.fire({
							title: 'เกิดข้อผิดพลาด',
							text: data ?? 'ไม่สามารถลบข้อมูลได้',
							icon: 'error',
							showConfirmButton: false,
							customClass: {
								confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
							},
							timer: 1500,
						})
					} else {
						Swal.fire({
							title: 'สำเร็จ',
							text: 'ลบข้อมูลเสร็จสิ้น',
							icon: 'success',
							showConfirmButton: false,
							customClass: {
								confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
							},
							timer: 1500,
						})
						fetchData()
					}
				})
			}
		})
	}

	useEffect(() => {
		fetchData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		setShowContentToolbar(true)
		setCustomContentToolbar(
			<Toolbar onCreate={createQuotation} />
			// <DateFilterToolbar
			// 	isUseDateFilter={isUseDateFilter}
			// 	onChangeDateFilter={onChangeDateFilter}
			// />
		)

		return () => {
			setShowContentToolbar(true)
			setCustomContentToolbar(null)
			clearState()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filter.startPeriod, filter.endPeriod, isUseDateFilter])

	return {
		isLoading,
		data,
		totalData: rawData.length,
		currentPage,
		goToPage,
		viewDetail,
		filter,
		onChangeFilter,
		onDelete,
	}
}

export default ViewModel
